import { IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridCellProps } from '@progress/kendo-react-grid';
import { DeleteGridButton } from './delete-grid-button.component';
import { MutationKeyConfig } from '../../api/api-helper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { UserPermissionName } from '../../models/user-permission-name';
import { GuardedComponent } from '../guarded-component';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuthorize } from '../../hooks/use-authorize';

interface GridActionPermission {
  view?: UserPermissionName;
  edit?: UserPermissionName;
  delete?: UserPermissionName;
  download?: UserPermissionName;
  export?: UserPermissionName;
}

interface GridEditCommandCellProps {
  CustomComponent?: any;
  gridCellProps: GridCellProps;
  actionPermission?: GridActionPermission;
  onViewClick?: (data: any) => void;
  onEditClick?: (data: any) => void;
  onDeleteClick?: (data: any) => void;
  onDeleted?: (data: any) => void;
  onDownloadClick?: (data: any) => void;
  deleteMutationConfig?: (item: any) => MutationKeyConfig;
}

export const GridCommandCell = ({
  CustomComponent,
  gridCellProps,
  actionPermission,
  onDeleted,
  deleteMutationConfig,
  onEditClick,
  onViewClick,
  onDeleteClick,
  onDownloadClick,
}: GridEditCommandCellProps) => {
  const handleEditClick = () => {
    onEditClick?.(gridCellProps.dataItem);
  };

  const handleDeleteClick = () => {
    onDeleteClick?.(gridCellProps.dataItem);
  };

  const handleViewClick = () => {
    onViewClick?.(gridCellProps.dataItem);
  };

  const handleDownloadClick = () => {
    onDownloadClick?.(gridCellProps.dataItem);
  };

  const isEditPermission = actionPermission?.edit ? useAuthorize([actionPermission?.edit]) : true;
  const isDownloadPermission = actionPermission?.download ? useAuthorize([actionPermission?.download]) : true;
  const isDeletePermission = actionPermission?.delete ? useAuthorize([actionPermission?.delete]) : true;
  const isViewPermission = actionPermission?.view ? useAuthorize([actionPermission?.view]) : true;
  return (
    <td className="stickyCloumn">
      <div className="actionWidth">
        {CustomComponent && <CustomComponent dataItem={gridCellProps.dataItem} />}
        {onEditClick && (
          <>
            <GuardedComponent requiredPermissions={actionPermission?.edit ? [actionPermission.edit] : []}>
              <IconButton style={{ padding: 5 }} aria-label="edit" onClick={handleEditClick}>
                <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                  <EditIcon className="btnEdit" />
                </Tooltip>
              </IconButton>
            </GuardedComponent>
            {!isEditPermission && (
              <Tooltip title={<Typography variant="body1">You do not have edit permission</Typography>}>
                <EditIcon style={{ color: 'gray' }} className="btnEdit" />
              </Tooltip>
            )}
          </>
        )}
        {onDownloadClick && (
          <>
            <GuardedComponent requiredPermissions={actionPermission?.download ? [actionPermission.download] : []}>
              <div className="delete-btn-container">
                <IconButton style={{ padding: 5 }} aria-label="delete" onClick={handleDownloadClick}>
                  <Tooltip title={<Typography variant="body1">Download</Typography>}>
                    <DownloadIcon className="btnEdit" />
                  </Tooltip>
                </IconButton>
              </div>
            </GuardedComponent>
            {!isDownloadPermission && (
              <Tooltip title={<Typography variant="body1">You do not have download permission</Typography>}>
                <DownloadIcon style={{ color: 'gray' }} className="btnEdit" />
              </Tooltip>
            )}
          </>
        )}
        {deleteMutationConfig && onDeleted && (
          <>
            <GuardedComponent requiredPermissions={actionPermission?.delete ? [actionPermission.delete] : []}>
              <DeleteGridButton
                data={gridCellProps.dataItem}
                deleteMutationConfig={deleteMutationConfig}
                onDeleted={onDeleted}
              />
            </GuardedComponent>
            {!isDeletePermission && (
              <Tooltip title={<Typography variant="body1">You do not have delete permission</Typography>}>
                <DeleteIcon style={{ color: 'gray' }} className="btnDelete" />
              </Tooltip>
            )}
          </>
        )}
        {onDeleteClick && (
          <>
            <GuardedComponent requiredPermissions={actionPermission?.delete ? [actionPermission.delete] : []}>
              <div className="delete-btn-container">
                <IconButton style={{ padding: 5 }} aria-label="delete" onClick={handleDeleteClick}>
                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                    <DeleteIcon className="btnDelete" />
                  </Tooltip>
                </IconButton>
              </div>
            </GuardedComponent>
            {!isDeletePermission && (
              <Tooltip title={<Typography variant="body1">You do not have delete permission</Typography>}>
                <DeleteIcon style={{ color: 'gray' }} className="btnDelete" />
              </Tooltip>
            )}
          </>
        )}
        {onViewClick && (
          <>
            <GuardedComponent requiredPermissions={actionPermission?.view ? [actionPermission.view] : []}>
              <IconButton style={{ padding: 5 }} aria-label="view" onClick={handleViewClick}>
                <Tooltip title={<Typography variant="body1">View</Typography>}>
                  <VisibilityIcon className="btnView" />
                </Tooltip>
              </IconButton>
            </GuardedComponent>
            {!isViewPermission && (
              <Tooltip title={<Typography variant="body1">View</Typography>}>
                <VisibilityIcon style={{ color: 'gray' }} className="btnView" />
              </Tooltip>
            )}
          </>
        )}
      </div>
    </td>
  );
};
